import React, { Component } from 'react';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import posed, { PoseGroup } from 'react-pose';
import { Element, scroller } from 'react-scroll';

import config from 'config';
import { formatCurrency } from 'utils';
import LargeScreen from 'common/large-screen';
import SmallScreen from 'common/small-screen';
import Markdown from 'common/markdown';
import Button from 'common/button';
import CustomAmount from './custom-amount';
import AmountCard from './amount-card';

const ContentPose = posed.div({
    enter: { opacity: 1, translateY: 0 },
    exit: { opacity: 0, translateY: 25 },
});

class ContributeSelectAmount extends Component {
    static propTypes = {
        onAmountSelect: PropTypes.func.isRequired,
        name: PropTypes.string.isRequired,
        presets: PropTypes.arrayOf(PropTypes.number).isRequired,
        isPledge: PropTypes.bool.isRequired,
        showFlash: PropTypes.func.isRequired,
        performanceEstimate: PropTypes.number,
        singularMetricName: PropTypes.string,
        pluralMetricName: PropTypes.string,
    };

    static defaultProps = {
        performanceEstimate: 0,
        singularMetricName: 'event',
        pluralMetricName: 'events',
    };

    state = {
        amount: '',
        isCustom: false,
    };

    get title() {
        const { isPledge, singularMetricName } = this.props;
        const { amount, isCustom } = this.state;
        const formattedAmount = formatCurrency(amount, { isCents: false });

        if (isPledge) {
            return this.hasAmount && !isCustom
                ? `You have selected ${formattedAmount} (US) per ${singularMetricName}`
                : `How much would you like to pledge per ${singularMetricName}?`;
        }

        return this.hasAmount && !isCustom
            ? `You have selected ${formattedAmount} (US)`
            : 'How much would you like to donate?';
    }

    get hasAmount() {
        const { amount } = this.state;
        return Boolean(amount);
    }

    get isBelowMinimum() {
        const { amount } = this.state;
        return Boolean(amount && +amount < this.contributionMinimumAsCents / 100);
    }

    get estimateText() {
        const { performanceEstimate, singularMetricName, pluralMetricName } = this.props;
        const { isCustom } = this.state;
        const properMetricName = performanceEstimate === 1 ? singularMetricName : pluralMetricName;

        if (!isCustom && !this.hasAmount) {
            return `Estimated totals are based on **${this.formattedEstimate} total ${properMetricName}.**`;
        }

        return `Based on an estimated **${this.formattedEstimate} ${properMetricName},** your total contribution will be **approximately ${this.formattedEstimatedTotals}.**`;
    }

    get formattedEstimate() {
        const { performanceEstimate } = this.props;
        return numeral(performanceEstimate).format('0,0[.]0');
    }

    get estimatedTotal() {
        const { isPledge, performanceEstimate } = this.props;
        return isPledge ? this.finalAmountAsCents * performanceEstimate : this.finalAmountAsCents;
    }

    get formattedEstimatedTotals() {
        return formatCurrency(this.estimatedTotal, { cents: 'never' });
    }

    get contributionMinimumAsCents() {
        const { isPledge } = this.props;
        return isPledge ? config('/minimum_pledge_amount') : config('/minimum_donation_amount');
    }

    // The amount (in cents) accounting for minimum charges
    get finalAmountAsCents() {
        const { amount } = this.state;
        return Math.max(Number(amount) * 100, this.contributionMinimumAsCents);
    }

    get options() {
        const { presets, isPledge, performanceEstimate } = this.props;

        return presets
            .sort((x, y) => -(x - y))
            .map((amount, i) => {
                const amountInDollars = amount / 100;

                const obj = {
                    amount: amountInDollars,
                    formattedAmount: formatCurrency(amountInDollars, { isCents: false }),
                };

                if (isPledge) {
                    const estimatedTotalInDollars = (amount * performanceEstimate) / 100;
                    obj.estimatedTotal = estimatedTotalInDollars;
                    obj.formattedEstimatedTotal = formatCurrency(estimatedTotalInDollars, {
                        isCents: false,
                        cents: 'never',
                    });
                    obj.tier = ['diamond', 'gold', 'silver', 'bronze'][i];
                }

                return obj;
            });
    }

    handleToggleCustom = () => this.setState(prev => ({ isCustom: !prev.isCustom, amount: '' }));

    handlePresetSelect = amount => this.setState({ amount });

    handleCustomAmount = amount => {
        this.setState({ amount });
    };

    handleContinue = () => {
        const { onAmountSelect, showFlash } = this.props;

        if (this.isBelowMinimum) {
            showFlash({
                type: 'warning',
                title: 'Amount Adjusted',
                message: `Your ${
                    this.isPledge ? 'pledge' : 'donation'
                } amount was adjusted to our minimum amount of ${formatCurrency(
                    this.contributionMinimumAsCents
                )}.`,
                duration: 6000,
            });
        }

        onAmountSelect(this.finalAmountAsCents, this.estimatedTotal);
    };

    render() {
        const { name, isPledge } = this.props;
        const { isCustom, amount } = this.state;

        return (
            <>
                <LargeScreen>
                    <Element name="js-scroll-presets" />
                </LargeScreen>
                <hgroup className="mb-8 md:mb-12 xxxl:mb-16">
                    <p className="text-16 text-gray-40 font-medium mb-1 md:mb-2 truncate">{name}</p>
                    <h1 className="font-black text-21 md:text-24 xxxl:text-28 text-gray-70">
                        {this.title}
                    </h1>
                    {isPledge && (
                        <Markdown
                            className="text-16 xxxl:text-18 text-gray-40 font-medium mt-6"
                            css={{
                                strong: {
                                    fontWeight: 'inherit',
                                    color: config('/tailwind/colors/gray70'),
                                },
                            }}
                        >
                            {this.estimateText}
                        </Markdown>
                    )}
                </hgroup>
                <PoseGroup withParent={false}>
                    {isCustom ? (
                        <ContentPose key="custom-amount">
                            <CustomAmount
                                value={amount}
                                onChange={this.handleCustomAmount}
                                onSubmit={this.handleContinue}
                            />
                        </ContentPose>
                    ) : (
                        <ContentPose key="preset-amount" className="-mx-4 lg:-mx-8">
                            <SmallScreen>
                                <Element name="js-scroll-presets" />
                            </SmallScreen>
                            <div className="flex flex-wrap -ml-12 -mt-8">
                                {this.options.map(option => (
                                    <div className="pl-12 pt-8 w-1/2 md:w-1/4" key={option.amount}>
                                        <AmountCard
                                            amount={option.formattedAmount}
                                            estimate={option.formattedEstimatedTotal}
                                            tier={option.tier}
                                            onSelect={() => {
                                                this.handlePresetSelect(option.amount);
                                                scroller.scrollTo('js-scroll-presets', {
                                                    duration: 500,
                                                    smooth: true,
                                                    offset: -100,
                                                });
                                            }}
                                            isSelected={option.amount === amount}
                                            isDeselected={
                                                this.hasAmount && option.amount !== amount
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                        </ContentPose>
                    )}
                </PoseGroup>
                <div className="mt-12 md:mt-16 text-center md:text-left">
                    <button
                        type="button"
                        className="link-green font-medium"
                        onClick={this.handleToggleCustom}
                        data-testid="amount-type-toggle"
                    >
                        {isCustom ? 'Back to preset amounts' : 'Or enter custom amount'}
                    </button>
                </div>
                <PoseGroup withParent={false}>
                    {this.hasAmount && (
                        <ContentPose
                            key="continue"
                            className="mt-12 md:mt-16 text-center md:text-left"
                        >
                            <Button
                                color="green"
                                size="md"
                                className="sm:fixed sm:pin-b sm:pin-x sm:rounded-none sm:w-full sm:py-8 sm:z-20 font-medium text-16"
                                onClick={this.handleContinue}
                                data-testid="submit"
                            >
                                Continue to payment
                            </Button>
                        </ContentPose>
                    )}
                </PoseGroup>
            </>
        );
    }
}

export default ContributeSelectAmount;
